/************************************************************************************
 * [COMPONENT]: L-HEADER
 * Controls main header functionality including cart interactions
 *************************************************************************************/

class LTAHeader extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		const isPDPPage = document.getElementById('main-content').classList.contains('template-product')

		if (isPDPPage) return

		this.initStickyMode()
	}

	initStickyMode() {
		window.addEventListener(
			'scroll',
			l_debounce(() => {
				const stickyThreshold = (this.parentElement?.offsetHeight || 0) + 100

				this.parentElement?.classList.toggle('is-sticky-mode', window.scrollY > stickyThreshold)
			}, 50)
		)
	}
}

customElements.define('l-header', LTAHeader)

/************************************************************************************
 * [COMPONENT]: L-HEADER-INLINE-MENU
 *
 *************************************************************************************/

if (!customElements.get('l-header-inline-menu')) {
	class LTAHeaderInline extends HTMLElement {
		constructor() {
			super()

			this.ANIMATION_DURATION = 400
			this._handleSummaryClick = this._handleSummaryClick.bind(this)
			this._handleDocumentClick = this._handleDocumentClick.bind(this)
			this._activeTrap = null // keep track of which element has trapFocus
		}

		connectedCallback() {
			this.detailsEls = Array.from(this.querySelectorAll('details'))

			// Attach listeners to all summaries
			this.detailsEls.forEach(async (detailsEl) => {
				const summary = detailsEl.querySelector('summary')
				if (summary) {
					summary.addEventListener('click', this._handleSummaryClick.bind(this, detailsEl))
					detailsEl.addEventListener('mouseenter', this._handleSummaryClick.bind(this, detailsEl))
					detailsEl.addEventListener('mouseleave', this._closeWithAnimation.bind(this, detailsEl))
				}
			})

			document.addEventListener('click', this._handleDocumentClick)
		}

		disconnectedCallback() {
			this.detailsEls?.forEach((detailsEl) => {
				const summary = detailsEl.querySelector('summary')
				if (summary) summary.removeEventListener('click', this._handleSummaryClick)
			})

			document.removeEventListener('click', this._handleDocumentClick)
		}

		async _handleSummaryClick(toggledEl, event) {
			event.preventDefault()

			const level = toggledEl.dataset.level
			const isOpened = toggledEl.hasAttribute('open')
			const openEls = this.detailsEls.filter((el) => el.dataset.level === level && el !== toggledEl && el.open)

			// Close all other open siblings first
			openEls.map((el) => this._closeWithAnimation(el))

			l_debounce(() => {
				this._openWithAnimation(toggledEl)
			}, this.ANIMATION_DURATION)()
		}

		_openWithAnimation(detailsEl) {
			const content = detailsEl.querySelector(':scope > *:not(summary)')
			if (!content) return

			detailsEl.setAttribute('open', '')
			content.style.height = '0px'
			content.style.overflow = 'hidden'
			content.style.transition = 'height 0.3s ease'

			requestAnimationFrame(() => {
				content.style.height = `${content.scrollHeight}px`
			})

			// Lock body scroll if the details element has a mega menu
			const hasMegaMenu = detailsEl.querySelector('[data-mega-menu]')
			if (hasMegaMenu) {
				l_lockBodyScroll('header-inline-menu')
			}
		}

		_closeWithAnimation(detailsEl) {
			const content = detailsEl.querySelector(':scope > *:not(summary)')
			if (!content) return Promise.resolve()

			// 🔁 Close nested details recursively
			const nestedDetails = Array.from(detailsEl.querySelectorAll(':scope details[open]'))
			const nestedPromises = nestedDetails.map((child) => this._closeWithAnimation(child))
			const startHeight = content.scrollHeight

			content.style.height = `${startHeight}px`
			content.style.overflow = 'hidden'
			content.style.transition = 'height 0.3s ease'

			requestAnimationFrame(() => {
				content.style.height = '0px'
			})

			// Unlock body scroll if the details element has a mega menu
			const hasMegaMenu = detailsEl.querySelector('[data-mega-menu]')
			if (hasMegaMenu) {
				l_unLockBodyScroll('header-inline-menu')
			}

			l_debounce(() => {
				detailsEl.removeAttribute('open')
				detailsEl.removeAttribute('height')
				detailsEl.removeAttribute('overflow')
				detailsEl.removeAttribute('transition')
			}, this.ANIMATION_DURATION)()
		}

		_handleDocumentClick(event) {
			if (this.contains(event.target)) return // ignore inside clicks

			const openEls = this.detailsEls.filter((el) => el.open)
			openEls.forEach((el) => this._closeWithAnimation(el))

			if (openEls.length > 0) {
				// clear trap focus globally
				l_removeTrapFocus()
				this._activeTrap = null
			}
		}
	}

	customElements.define('l-header-inline-menu', LTAHeaderInline)
}

/************************************************************************************
 * [MODULE]: MOBILE MENU DRAWERS
 * Controls mobile menu drawers functionality
 *************************************************************************************/

class LTAMainMenuDrawer extends LTADrawer {
	// To keep the focus on the trigger element based on the race condition
	drawer_close() {
		super.drawer_close()
	}
}

customElements.define('l-main-menu-drawer', LTAMainMenuDrawer)

